import type { LoadedCustomComponentModule } from './hostRuntime';

/**
 * Stateful compiled-artifact loader shared by `CustomComponentProvider` (in the React tree) and
 * Vike's `+onBeforeRenderClient` (before the tree mounts). Owns the module / blob URL caches that
 * dedupe across slugs and re-entrancy.
 */
export interface CustomComponentLoaderManifest {
  slugToCurrentSourceHash: Record<string, string>;
}

/**
 * A resolver returned `null` (staticAssetsBaseURL not available). Thrown instead of fetching so a
 * missing base can never degrade into a doomed `https://undefined/...` request. Deterministic —
 * `isRetriableLoadError` deliberately rejects this message, so the retry loop never re-runs it.
 */
export class CustomComponentAssetUrlUnavailableError extends Error {
  constructor(asset: 'compiled artifact' | 'runtime shim', ref: string) {
    super(
      `Custom-component ${asset} for ${ref} cannot load: staticAssetsBaseURL is not available, so no URL was constructed.`,
    );
    this.name = 'CustomComponentAssetUrlUnavailableError';
  }
}

export interface CustomComponentLoaderOptions {
  /** Returns `null` when the static-assets base has not resolved — the load is then skipped. */
  resolveCompiledUrl: (sourceHash: string) => string | null;
  /** Returns `null` when the static-assets base has not resolved — the import is then skipped. */
  resolveRuntimeUrl: (specifier: string) => string | null;
  // Getter so the editor's mutable manifest stays live without rebuilding the loader.
  getManifest: () => CustomComponentLoaderManifest;
  /**
   * Loads + registers the diagram/animation libraries on the host runtime. Awaited before importing
   * an artifact that references non-react runtime libraries, so the artifact's shim finds its module.
   * Omitted by callers that don't support runtime libraries (the artifact then fails loudly).
   */
  ensureRuntimeLibraries?: (slugs: readonly string[]) => Promise<void>;
}

export interface CustomComponentLoader {
  loadByHash: (sourceHash: string) => Promise<LoadedCustomComponentModule>;
  loadBySlug: (slug: string) => Promise<LoadedCustomComponentModule>;
  /** Sync lookup for hashes whose promise has already resolved; returns `null` otherwise. */
  getResolvedByHash: (sourceHash: string) => LoadedCustomComponentModule | null;
  // Blob URL → sourceHash, used by the provider's window-error attribution.
  blobUrlToSourceHash: ReadonlyMap<string, string>;
}

const COMPONENT_IMPORT_PATTERN = /^components\/([a-z0-9][a-z0-9-]*)$/;
const RUNTIME_PATH_PREFIX = '/__mutiny/runtime/';
const RUNTIME_SLUG_PATTERN = /^\/__mutiny\/runtime\/([\w-]+)\//;

// Cross-origin bundle/shim fetches fail transiently; retry before surfacing an error.
const MAX_LOAD_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 150;
const PROBE_TIMEOUT_MS = 3000;

export function createCustomComponentLoader(options: CustomComponentLoaderOptions): CustomComponentLoader {
  const moduleCache = new Map<string, Promise<LoadedCustomComponentModule>>();
  const resolvedCache = new Map<string, LoadedCustomComponentModule>();
  const blobUrlCache = new Map<string, Promise<string>>();
  const blobUrlToSourceHash = new Map<string, string>();
  // Rewritten http(s) deps (the runtime-shim URL) per artifact, so a failed import() can probe
  // which one broke rather than surface the opaque browser message.
  const externalDepsByHash = new Map<string, string[]>();
  const inFlight = new Set<string>();
  // Runtime-library slugs by artifact. Building a blob recursively rewrites sibling imports, so the
  // entry's set includes transitive component dependencies without making later loads inherit it.
  const runtimeLibSlugsByHash = new Map<string, ReadonlySet<string>>();

  async function loadOnce(sourceHash: string): Promise<LoadedCustomComponentModule> {
    const blobUrl = await getBlobUrl(sourceHash);
    // Register diagram/animation libraries before importing — the artifact's shim reads them off the
    // host runtime synchronously during import. Only pages with such a component pay this cost.
    const runtimeSlugs = [...(runtimeLibSlugsByHash.get(sourceHash) ?? new Set<string>())].filter(
      (slug) => slug !== 'react',
    );
    if (runtimeSlugs.length > 0 && options.ensureRuntimeLibraries) {
      await options.ensureRuntimeLibraries(runtimeSlugs);
    }
    try {
      return (await import(/* @vite-ignore */ /* webpackIgnore: true */ blobUrl)) as LoadedCustomComponentModule;
    } catch (importError) {
      // "Failed to fetch dynamically imported module" names only the blob; enrich it with the
      // transitive dependency that actually failed.
      throw await enrichImportError(sourceHash, importError);
    }
  }

  async function loadByHash(sourceHash: string): Promise<LoadedCustomComponentModule> {
    const cached = moduleCache.get(sourceHash);
    if (cached) return cached;
    const promise = (async () => {
      let lastError: unknown;
      for (let attempt = 1; attempt <= MAX_LOAD_ATTEMPTS; attempt += 1) {
        try {
          const mod = await loadOnce(sourceHash);
          resolvedCache.set(sourceHash, mod);
          return mod;
        } catch (error) {
          lastError = error;
          if (attempt >= MAX_LOAD_ATTEMPTS || !isRetriableLoadError(error)) throw error;
          // Re-fetch fresh; the browser caches a failed transitive fetch against the blob URL.
          blobUrlCache.delete(sourceHash);
          await delay(RETRY_BASE_DELAY_MS * attempt);
        }
      }
      throw lastError;
    })();
    moduleCache.set(sourceHash, promise);
    promise.catch(() => {
      if (moduleCache.get(sourceHash) === promise) moduleCache.delete(sourceHash);
    });
    return promise;
  }

  async function loadBySlug(slug: string): Promise<LoadedCustomComponentModule> {
    const manifest = options.getManifest();
    const sourceHash = manifest.slugToCurrentSourceHash[slug];
    if (!sourceHash) {
      throw new Error(`Custom component slug "${slug}" is not registered for this page.`);
    }
    return loadByHash(sourceHash);
  }

  async function getBlobUrl(sourceHash: string): Promise<string> {
    const cached = blobUrlCache.get(sourceHash);
    if (cached) return cached;
    if (inFlight.has(sourceHash)) {
      throw new Error(`Circular custom-component import detected at sourceHash ${sourceHash}.`);
    }
    inFlight.add(sourceHash);
    const promise = (async () => {
      const compiledUrl = options.resolveCompiledUrl(sourceHash);
      if (compiledUrl === null) throw new CustomComponentAssetUrlUnavailableError('compiled artifact', sourceHash);
      const text = await fetchArtifactText(compiledUrl);
      const { code, externalDependencyUrls, runtimeLibSlugs } = await rewriteBareSpecifiers(text);
      externalDepsByHash.set(sourceHash, externalDependencyUrls);
      runtimeLibSlugsByHash.set(sourceHash, runtimeLibSlugs);
      const url = URL.createObjectURL(new Blob([code], { type: 'text/javascript' }));
      blobUrlToSourceHash.set(url, sourceHash);
      return url;
    })();
    blobUrlCache.set(sourceHash, promise);
    promise
      .catch(() => {
        if (blobUrlCache.get(sourceHash) === promise) blobUrlCache.delete(sourceHash);
      })
      .finally(() => {
        inFlight.delete(sourceHash);
      });
    return promise;
  }

  async function rewriteBareSpecifiers(
    text: string,
  ): Promise<{ code: string; externalDependencyUrls: string[]; runtimeLibSlugs: ReadonlySet<string> }> {
    const candidates: Array<{ matchText: string; spec: string }> = [];
    for (const regex of [/(\bfrom\s*['"])([^'"\n]+)(['"])/g, /(\bimport\s*['"])([^'"\n]+)(['"])/g]) {
      for (const m of text.matchAll(regex)) {
        const matchText = m[0];
        const spec = m[2];
        if (matchText && spec) candidates.push({ matchText, spec });
      }
    }
    const runtimeLibSlugs = new Set<string>();
    // Resolve in parallel — each resolution either hits the per-sourceHash cache or recursively
    // builds a child blob URL. Since the cache promise is set before recursion, concurrent calls
    // for the same slug share one resolution.
    const resolutions = await Promise.all(
      candidates.map(async ({ matchText, spec }) => {
        const replacement = await resolveSpecifier(spec, runtimeLibSlugs);
        return replacement && replacement !== spec ? { matchText, spec, replacement } : null;
      }),
    );
    // Only http(s) deps are probeable; sibling components are in-memory blob: URLs.
    const externalDependencyUrls = [
      ...new Set(resolutions.flatMap((r) => (r && /^https?:/i.test(r.replacement) ? [r.replacement] : []))),
    ];
    // Apply replacements after resolution so we don't re-match rewritten URLs.
    const code = resolutions.reduce<string>((acc, r) => {
      if (!r) return acc;
      return acc.replace(r.matchText, r.matchText.replace(r.spec, r.replacement));
    }, text);
    return { code, externalDependencyUrls, runtimeLibSlugs };
  }

  async function enrichImportError(sourceHash: string, importError: unknown): Promise<Error> {
    const original = importError instanceof Error ? importError : new Error(String(importError));
    const deps = externalDepsByHash.get(sourceHash) ?? [];
    if (deps.length === 0) return original;
    const probes = await Promise.all(deps.map(probeDependency));
    const enriched = new Error(`${original.message} | dependency probe: ${probes.join('; ')}`);
    // Keep the original name + stack (its blob: frame drives the provider's error attribution).
    enriched.name = original.name;
    enriched.stack = original.stack;
    return enriched;
  }

  async function resolveSpecifier(spec: string, runtimeLibSlugs: Set<string>): Promise<string | null> {
    if (spec.startsWith(RUNTIME_PATH_PREFIX)) {
      const slug = RUNTIME_SLUG_PATTERN.exec(spec)?.[1];
      if (slug) runtimeLibSlugs.add(slug);
      const runtimeUrl = options.resolveRuntimeUrl(spec);
      if (runtimeUrl === null) throw new CustomComponentAssetUrlUnavailableError('runtime shim', spec);
      return runtimeUrl;
    }
    const componentMatch = COMPONENT_IMPORT_PATTERN.exec(spec);
    if (componentMatch) {
      const slug = componentMatch[1];
      if (!slug) return null;
      const sourceHash = options.getManifest().slugToCurrentSourceHash[slug];
      if (!sourceHash) {
        throw new Error(`Custom component "${slug}" referenced by an artifact is not registered for this page.`);
      }
      const blobUrl = await getBlobUrl(sourceHash);
      for (const runtimeSlug of runtimeLibSlugsByHash.get(sourceHash) ?? []) {
        runtimeLibSlugs.add(runtimeSlug);
      }
      return blobUrl;
    }
    return null;
  }

  return {
    loadByHash,
    loadBySlug,
    getResolvedByHash: (sourceHash) => resolvedCache.get(sourceHash) ?? null,
    blobUrlToSourceHash,
  };
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

// Retry transient fetch failures only — a real component code error is deterministic. Exported for tests.
export function isRetriableLoadError(error: unknown): boolean {
  const message = (error instanceof Error ? error.message : String(error)).toLowerCase();
  return (
    message.includes('failed to fetch') || // network / artifact HTTP error / dynamic-import fetch (Chrome)
    message.includes('error loading dynamically imported module') || // Firefox
    message.includes('importing a module script failed') // Safari
  );
}

async function fetchArtifactText(url: string): Promise<string> {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.text();
  } catch (cause) {
    throw new Error(
      `Failed to fetch custom-component artifact at ${url}: ${cause instanceof Error ? cause.message : String(cause)}`,
      { cause },
    );
  }
}

async function probeDependency(url: string): Promise<string> {
  // The probe runs after the import already failed (often a network issue), so the probe itself can
  // hang — time it out so the enriched error still surfaces promptly.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    const res = await fetch(url, { signal: controller.signal });
    const contentType = res.headers.get('content-type') ?? 'no content-type';
    return `${url} -> HTTP ${res.status}${res.ok ? '' : ' (not ok)'} (${contentType})`;
  } catch (probeError) {
    if (controller.signal.aborted) return `${url} -> no response within ${PROBE_TIMEOUT_MS}ms`;
    return `${url} -> network error: ${probeError instanceof Error ? probeError.message : String(probeError)}`;
  } finally {
    clearTimeout(timer);
  }
}
